/**
 * The running-job bar (FM Technician rebuild B27, C1): the one job the
 * technician has running, on every page, with its timer — tap to open it.
 *
 * It reads GET /tech/home's `running_job`: the caller's own job, started and
 * not on hold (the server decides; a held job does not run). The timer is the
 * time since the start MINUS the time the job sat on hold since then
 * (`held_seconds`), so a two-hour wait for a vendor does not read as two hours
 * of work. It ticks every second on the phone's clock from the server's start.
 *
 * Green-soft strip under the top bar (DESIGN-CHECK §2, after EmergencyBanner's
 * layout), 48 px tall, 14 px text, a word ("Running") and an icon beside the
 * colour. Renders nothing when nothing runs.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircleIcon } from '@heroicons/react/24/solid';
import { canOpenRoute } from '@/config/routeAccess';
import { techJobPath } from '@/config/techExperience';
import { useAuthStore } from '@/stores/authStore';
import type { RunningJob } from './techApi';

/** Seconds worked: now − start − time held since the start, never below 0. */
export function workedSeconds(job: Pick<RunningJob, 'started_at' | 'held_seconds'>, now: number): number {
  const started = Date.parse(job.started_at);
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.floor((now - started) / 1000) - Math.max(0, job.held_seconds || 0));
}

/** "0:07:05", "12:30:00" — hours, then two-digit minutes and seconds. */
export function formatTimer(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function RunningJobBar({ job }: { job: RunningJob | null | undefined }) {
  const user = useAuthStore((s) => s.user);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!job) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [job]);

  if (!job) return null;
  const timer = formatTimer(workedSeconds(job, now));
  const to = techJobPath(job.kind, job.id, (p) => canOpenRoute(user, p));
  const body = (
    <>
      <PlayCircleIcon className="h-6 w-6 shrink-0 text-success-700" aria-hidden="true" />
      <span className="shrink-0 font-semibold">Running</span>
      <span className="shrink-0 font-mono" data-testid="running-job-ref">{job.ref}</span>
      <span className="min-w-0 flex-1 truncate text-content-secondary">{job.title}</span>
      <span className="shrink-0 font-mono tabular-nums font-semibold" data-testid="running-job-timer"
            aria-label={`Worked ${timer}, time on hold not counted`}>
        {timer}
      </span>
    </>
  );
  const cls = 'flex min-h-12 w-full items-center gap-2 border-b border-success-200 bg-success-50 px-4 text-sm text-success-800 lg:px-6';
  // No page this person can open (cannot happen for a technician's own WO or
  // ticket, but never a dead link): the bar still says what is running.
  if (!to) return <div className={cls} data-testid="running-job-bar" role="status">{body}</div>;
  return (
    <Link to={to} className={cls} data-testid="running-job-bar"
          aria-label={`Running job ${job.ref}: ${job.title}, ${timer}. Open it.`}>
      {body}
    </Link>
  );
}
